import React, { useState, useEffect, useLayoutEffect } from 'react';
import styles from './Benchmark.module.scss';
import { useAppSelector } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import Icon from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import { CurrencyFormatter } from '../../../utils/currencyFormatter';
import { NumberFormatter } from '../../../utils/NumberFormatter';

type BenchmarkProps = {
  minValue: number;
  maxValue: number;
  averageValue?: number; // Value to place the average line
  medianValue?: number; // Value to place the middle line
  currentBenchmarkValue: number; // Value to place the marker
  quartileLine: [number, number]; // Array containing two values for quartile start and end
  isPercentValue: boolean | undefined;
  benchmarkDataType?: 'percentage' | 'number' | 'currency' | '';
  currency?: string;
  customHeaderKey?: string | null;
  truncateDecimals?: boolean;
};

const Benchmark: React.FC<BenchmarkProps> = ({
  minValue = 0,
  maxValue = 0,
  medianValue,
  averageValue,
  quartileLine,
  currentBenchmarkValue = 0,
  benchmarkDataType = '',
  currency = '',
  customHeaderKey = null,
  truncateDecimals = false
}) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);
  const [isAnimated, setIsAnimated] = useState(false);
  const [hoveredMetrics, setHoveredMetrics] = useState<string[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState({
    left: 0,
    top: 0,
    arrowLeft: '50%',
  });
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (showTooltip && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      const x = tooltipPosition.x;
      const width = rect.width;
      const padding = 20;

      let left = x - width / 2;

      if (left < padding) {
        left = padding;
      } else if (left + width > screenWidth - padding) {
        left = screenWidth - padding - width;
      }

      let arrowLeft = x - left;
      if (arrowLeft < 12) arrowLeft = 12;
      if (arrowLeft > width - 12) arrowLeft = width - 12;

      setTooltipStyle({
        left,
        top: tooltipPosition.y,
        arrowLeft: `${arrowLeft}px`,
      });
    }
  }, [showTooltip, tooltipPosition]);

  useEffect(() => {
    setIsAnimated(true);
  }, []);

  const shouldMarkerHide = !(Math.abs(minValue) === 0 && Math.abs(maxValue) === 0);
  const getPercentage = (value: number) => ((value - minValue) / (maxValue - minValue)) * 100;

  const currentBenchmarkValueInternal =
    truncateDecimals && currentBenchmarkValue !== undefined
      ? Math.round(currentBenchmarkValue)
      : currentBenchmarkValue;

  const midPos = getPercentage(medianValue ?? 0);
  const avgPos = getPercentage(averageValue ?? 0);
  const quartileArray = quartileLine ?? [];
  const [quartile1, quartile2] = quartileArray;
  const quartile1Pos = getPercentage(quartile1);
  const quartile2Pos = getPercentage(quartile2);
  const markerPos = getPercentage(currentBenchmarkValueInternal);

  const formatValue = (value: number | undefined) => {
    if (value === undefined || value === null) return null;
    if (benchmarkDataType === 'currency') {
      return String(CurrencyFormatter(currency, value)).replace(/\.00$/, '');
    } else if (benchmarkDataType === 'number') {
      return NumberFormatter(userAffiliateCode, value, truncateDecimals);
    } else if (benchmarkDataType === 'percentage') {
      return value + '%';
    }
    return value.toString();
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!trackRef.current) return;

    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;

    setTooltipPosition({ x: e.clientX, y: rect.top });
    setShowTooltip(true);

    // Determine which metric is closest to cursor for highlighting
    const tolerance = 5;
    let closestMetrics: string[] = [];
    let minDistance = Infinity;

    const checkMetric = (metricKey: string, position: number) => {
      const distance = Math.abs(percentage - position);
      if (distance < tolerance) {
        if (distance < minDistance - 0.01) {
          minDistance = distance;
          closestMetrics = [metricKey];
        } else if (Math.abs(distance - minDistance) < 0.01) {
          closestMetrics.push(metricKey);
        }
      }
    };

    checkMetric('min', 0);
    checkMetric('max', 100);
    checkMetric('current', markerPos);

    if (averageValue !== undefined) {
      checkMetric('marketAverage', avgPos);
    }

    if (medianValue !== undefined) {
      checkMetric('median', midPos);
    }

    if (quartile1Pos > 0) {
      checkMetric('lowerQuartile', quartile1Pos);
    }

    if (quartile2Pos > 0) {
      checkMetric('upperQuartile', quartile2Pos);
    }

    setHoveredMetrics(closestMetrics);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
    setHoveredMetrics([]);
  };

  const renderTooltipContent = (highlightKey?: string) => {
    const metrics: {
      key: string;
      label: string;
      value: number | undefined;
      custom_style?: string;
    }[] = [];
    metrics.push({
      key: 'min',
      label: t('benchmarkLabels.min'),
      value: minValue,
    });

    if (quartile1 !== undefined) {
      metrics.push({
        key: 'lowerQuartile',
        label: t('benchmarkLabels.lowerQuartile'),
        value: quartile1,
      });
    }

    if (medianValue !== undefined) {
      metrics.push({
        key: 'median',
        label: t('benchmarkLabels.median'),
        value: medianValue,
        custom_style: styles.aiblue_color,
      });
    }

    if (averageValue !== undefined) {
      metrics.push({
        key: 'marketAverage',
        label: t('benchmarkLabels.marketAverage'),
        value: averageValue,
      });
    }

    if (quartile2 !== undefined) {
      metrics.push({
        key: 'upperQuartile',
        label: t('benchmarkLabels.upperQuartile'),
        value: quartile2,
      });
    }

    metrics.push({
      key: 'current',
      label: t('benchmarkLabels.current'),
      value: currentBenchmarkValueInternal,
      custom_style: styles.brand_color,
    });

    metrics.push({
      key: 'max',
      label: t('benchmarkLabels.max'),
      value: maxValue,
    });

    return (
      <div className={styles.benchmark_tooltip_content}>
        {metrics.map((metric) => (
          <div
            key={metric.key}
            className={`${styles.benchmark_tooltip_metric} ${metric.custom_style ?? ''} ${hoveredMetrics.includes(metric.key) ? styles.benchmark_tooltip_metric_highlighted : ''}`}
          >
            <span className={styles.benchmark_tooltip_label}>{metric.label}</span>
            <span className={styles.benchmark_tooltip_value}>{formatValue(metric.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  /* Calculation for label positioning and overlap below the track */
  const minLabel = formatValue(minValue);
  const maxLabel = formatValue(maxValue);
  const medianLabel = formatValue(medianValue);

  const minLabelLength = minLabel ? String(minLabel).length : 0;
  const maxLabelLength = maxLabel ? String(maxLabel).length : 0;

  const minThreshold = minLabelLength * 2.5;
  const maxThreshold = 100 - maxLabelLength * 2.5;

  const showMedianLabel =
    medianValue !== undefined &&
    medianLabel !== minLabel &&
    medianLabel !== maxLabel &&
    midPos > minThreshold &&
    midPos < maxThreshold;

  return (
    <div className={`${styles.benchmark_wrapper} ${isAnimated ? styles.animated : ''}`}>
      <div className={`${styles.benchmark_title_section}`}>
        <span>{customHeaderKey ? t(customHeaderKey) : t('benchmark')}</span>
        <Tooltip
          content={
            <>
              {t('tooltip.0')}
              <span style={{ fontWeight: 'bold' }}> {t('tooltip.1')} </span>
              {t('tooltip.2')}
            </>
          }
          width="257"
          height="auto"
          bottom="30px"
          left="685%"
        >
          <span>
            <Icon
              name="info_icon"
              size={16}
              color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
            />
          </span>
        </Tooltip>
      </div>
      <div className={`${styles.benchmark}`}>
        <div
          ref={trackRef}
          className={`${styles.benchmark_track}`}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <div className={`${styles.benchmark_sections}`}>
            {/* Three sections with a middle solid line */}

            {shouldMarkerHide && quartile1Pos > 0 && quartile2Pos > 0 && (
              <div
                className={`${styles.benchmark_quartile_block}`}
                style={{
                  left: `${quartile1Pos}%`,
                  width: `${quartile2Pos - quartile1Pos}%`,
                }}
              ></div>
            )}

            {/* Middle line for median value */}
            {shouldMarkerHide && medianValue !== undefined && (
              <div
                className={`${styles.median} ${styles.benchmark_section}`}
                style={{ left: `${midPos}%` }}
              ></div>
            )}

            {/* Middle line for average value */}
            {shouldMarkerHide && averageValue !== undefined && (
              <div
                className={`${styles.average} ${styles.benchmark_section}`}
                style={{ left: `${avgPos}%` }}
              ></div>
            )}
            <div className={`${styles.benchmark_section}`}></div>
          </div>

          {/* Dynamic marker (map pin) */}
          {shouldMarkerHide && (
            <div className={`${styles.benchmark_marker_wrapper}`} style={{ left: `${markerPos}%` }}>
              <div className={`${styles.benchmark_marker}`}>
                <Icon
                  name="benchmark_marker"
                  size={21}
                  color={currentTheme === 'light' ? '#5969D6' : '#99B2F5'}
                />
              </div>
            </div>
          )}
        </div>

        {/* Floating Tooltip */}
        {showTooltip && shouldMarkerHide && (
          <div
            ref={tooltipRef}
            className={styles.benchmark_floating_tooltip}
            style={
              {
                left: `${tooltipStyle.left}px`,
                top: `${tooltipStyle.top}px`,
                '--arrow-left': tooltipStyle.arrowLeft,
              } as React.CSSProperties
            }
          >
            {renderTooltipContent()}
          </div>
        )}

        {/* Labels below the track */}
        <div className={`${styles.benchmark_labels}`}>
          <span>{minLabel}</span>
          {showMedianLabel && (
            <span
              className={styles.mid_values}
              style={{ left: `${midPos}%`, transform: 'translateX(-50%)' }}
            >
              {medianLabel}
            </span>
          )}
          <span>{maxLabel}</span>
        </div>
      </div>
    </div>
  );
};

export default Benchmark;
